import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getRunHistoryData,
  getRunHistoryLoadingState,
} from "ee/selectors/workflowRunHistorySelectors";
import { Button, SegmentedControl, Tooltip } from "@appsmith/ads";
import styled from "styled-components";
import { fetchWorkflowRunHistory } from "ee/actions/workflowRunHistoryActions";
import { RunHistoryListItem } from "./RunHistoryListItem";
import { HistoryStateFilterStates } from "./helpers";
import type { DefaultRootState } from "react-redux";
import {
  createMessage,
  LOADING,
  WORKFLOW_RUN_HISTORY_REFRESH_TOOLTIP,
  WORKFLOW_RUN_HISTORY_TAB_LABEL_ALL_RUNS,
  WORKFLOW_RUN_HISTORY_TAB_LABEL_FAILED_RUNS,
} from "ee/constants/messages";

const RunHistoryListContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  gap: 4px;
  flex-grow: 1;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 8px;
`;

const StyledSegmentedControl = styled(SegmentedControl)`
  flex: 1;
  min-width: 0;
`;

const RefreshControl = styled.div`
  display: flex;
  flex-shrink: 0;
`;

interface RunHistoryListProps {
  selectedRunId: string;
  setSelectedRunId: (runId: string) => void;
  workflowId: string;
}

export function RunHistoryList({
  selectedRunId,
  setSelectedRunId,
  workflowId,
}: RunHistoryListProps) {
  const dispatch = useDispatch();
  const isLoading = useSelector(getRunHistoryLoadingState);
  const data = useSelector((state: DefaultRootState) =>
    getRunHistoryData(state, workflowId),
  );
  const [selectedFilter, setSelectedFilter] = useState(
    HistoryStateFilterStates.ALL_RUNS,
  );
  const [refreshingWorkflowId, setRefreshingWorkflowId] = useState<
    string | null
  >(null);
  const isRefreshing = refreshingWorkflowId === workflowId;
  const isFetchInProgress = isLoading || isRefreshing;
  const showLoadingState = isLoading && !isRefreshing;

  useEffect(() => {
    dispatch(fetchWorkflowRunHistory(workflowId, selectedFilter));
  }, [dispatch, selectedFilter, workflowId]);
  useEffect(
    function clearRunHistoryRefresh() {
      if (!isLoading) {
        setRefreshingWorkflowId(null);
      }
    },
    [isLoading],
  );
  useEffect(() => {
    if (data.length) {
      setSelectedRunId(data[0].workflowRunId);
    }
  }, [data, setSelectedRunId]);

  const handleRefresh = () => {
    if (isFetchInProgress) return;

    setRefreshingWorkflowId(workflowId);
    dispatch(fetchWorkflowRunHistory(workflowId, selectedFilter));
  };

  return (
    <>
      <FilterRow>
        <StyledSegmentedControl
          aria-disabled={isFetchInProgress}
          onChange={(value) => {
            if (isFetchInProgress) return;

            setSelectedFilter(value);
          }}
          options={[
            {
              label: createMessage(WORKFLOW_RUN_HISTORY_TAB_LABEL_ALL_RUNS),
              value: HistoryStateFilterStates.ALL_RUNS,
            },
            {
              label: createMessage(WORKFLOW_RUN_HISTORY_TAB_LABEL_FAILED_RUNS),
              value: HistoryStateFilterStates.FAILED_RUNS,
            },
          ]}
          value={selectedFilter}
        />
        <RefreshControl>
          <Tooltip
            content={createMessage(WORKFLOW_RUN_HISTORY_REFRESH_TOOLTIP)}
          >
            <Button
              aria-label={createMessage(WORKFLOW_RUN_HISTORY_REFRESH_TOOLTIP)}
              data-testid="t--run-history-refresh"
              isDisabled={isFetchInProgress}
              isIconButton
              kind="tertiary"
              onClick={handleRefresh}
              size="sm"
              startIcon="refresh"
            />
          </Tooltip>
        </RefreshControl>
      </FilterRow>
      {showLoadingState ? (
        <div>{createMessage(LOADING)}</div>
      ) : (
        <RunHistoryListContainer data-testid="t--run-history-list">
          {data.map((run) => (
            <RunHistoryListItem
              data={run}
              key={run.workflowRunId}
              onClick={() => setSelectedRunId(run.workflowRunId)}
              selected={run.workflowRunId === selectedRunId}
            />
          ))}
        </RunHistoryListContainer>
      )}
    </>
  );
}
